/**
 * Turning a contact entry from `ListContacts` into a row on the contacts screen.
 *
 * Shared 1:1 with iOS `native-apps/ios/Symphony/Store/ContactRows.swift` and Android
 * `native-apps/android/app/src/main/java/com/wix/symphony/ContactRows.kt`. The rules below are a
 * cross-platform contract; change one and change all three.
 *
 * The server decides everything that depends on data the client cannot see: every thread's STAGE
 * (`OutreachThreadRow.stage`, read through `lib/outreach-stage.ts` — the one place a stage becomes a dot,
 * a line or a group), which open loop to surface, and when the last activity was. What is left here is
 * presentation that three platforms would otherwise each invent — names, the shortened relative time on
 * a row, the Inbox rows, and the two sections the Contacts list is split into.
 *
 * The chips are client-side on purpose. The endpoint takes no filter, so a server-side chip could only
 * trim the page it had already fetched and would report "none" for a person whose thread sat outside
 * it. Every entry already carries its stage, so filtering here is both correct and free.
 */

import { contactStage, groupByStage, parseAuthor, stageSortTime, threadStage, threadTime, uniqueThreads, type OutreachStage, type StageThread } from './outreach-stage';

/** The subset of a `ContactCard` these rules read. */
export type ContactRowCard = {
  personId?: string;
  displayName?: string;
  labels?: string[];
  lastTouchAt?: string | Date;
  needsAttention?: boolean;
  attentionSummary?: string;
  openLoopCount?: number;
};

/** The subset of a `ContactWithOutreaches` these rules read. */
export type ContactRowEntry = {
  contact?: ContactRowCard;
  lastActivityAt?: string | Date;
  waitingOnMe?: boolean;
  waitingCount?: number;
  /** `OUTREACH_STAGE_*` — the server's verdict on the whole contact. See `contactStage`. */
  stage?: string;
  // The outreach threads (rows only, no message bodies — those are the person-scoped read) the list
  // carries per contact, so the "Needs you" partition can ask a contact's stage without a second fetch.
  outbound?: ChannelThread[];
  inbound?: ChannelThread[];
  /** Present for a contact the people book knows only by an address; the seen-key falls back to it. */
  recipientId?: string;
};

/**
 * The name to show for a contact — used by the list row AND the detail header, so the two never
 * disagree. The fallback order, a cross-platform contract:
 *
 *   1. the card's real display name;
 *   2. `recipientName` — the name the OUTREACH carries for this counterparty (`recipientName` off the
 *      `/api/outreaches` row, the bridge's sender name on a received one), when the caller holds an
 *      outreach. The server already fills the card from the same metadata, so this is the defence for
 *      a server that predates that, not the main path: an address-less recipient used to render
 *      "Unnamed contact" while the outreach beside it knew it was "ערבה";
 *   3. a reachable channel value (a counterparty the people book knows only by an address shows that
 *      address);
 *   4. the localized "Unnamed contact" (`unnamed`, i.e. `t('contacts.row.unnamed')`) — never a blank
 *      line beside an empty avatar.
 *
 * A name beats an address because a name is what a person recognises; an address is the last thing
 * that is still TRUE about them. 1:1 with the native `contactDisplayName` helpers.
 */
export function contactDisplayName(
  card: { displayName?: string } | undefined,
  channels: { value?: string }[] | undefined,
  unnamed: string,
  recipientName?: string,
): string {
  return (
    card?.displayName?.trim() ||
    recipientName?.trim() ||
    channels?.find((channel) => channel.value?.trim())?.value?.trim() ||
    unnamed
  );
}

/**
 * Which chip the list is filtered by.
 *
 * There is ONE attention chip, and it is the same rule as the "Needs you" SECTION below — a contact
 * whose stage is `needsYou` — so the chip and the section can never select different people.
 */
export type ContactChip = 'all' | 'new' | 'action-required' | 'needs-attention';

/**
 * Whether a person has NEW correspondence on this device: their last activity is later than the device's
 * seen-mark for them (`lib/contact-seen.ts`), or — never opened here — within the last day. Pure; the
 * same rule on all three surfaces, each with its own per-device mark.
 */
export function isNewContact(entry: { lastActivityAt?: string | Date }, seenAt: number | null, now: number): boolean {
  if (!entry.lastActivityAt) return false;
  const at = new Date(entry.lastActivityAt).getTime();
  if (!Number.isFinite(at)) return false;
  return at > (seenAt ?? now - 24 * 60 * 60 * 1000);
}

// Initials are deliberately NOT here. `lib/avatar-color.ts` already owns them app-wide — first letter
// of the first two words — and its own comment records that iOS and Android mirror it. A second rule
// on this screen would give the same person one monogram in chat and a different one in contacts.

/**
 * The short relative time a row shows — "2h", "3d", "5w".
 *
 * Deliberately terse and never "now": the row is scanned, not read, and a column that changes width
 * between "just now" and "2d" makes the list jitter. Anything under an hour is "now" only in the
 * long form below; here it collapses to minutes so the column stays two or three characters.
 *
 * Returns an empty string when there is no timestamp, which is a real state — a person nobody has
 * spoken to yet — and must render as blank rather than as the epoch.
 */
export function shortRelativeTime(at: string | Date | undefined, now: Date = new Date(), nowLabel?: string): string {
  const then = toDate(at);
  if (!then) return '';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  // Under a minute reads as "Now" when the caller passes the localized label (a just-sent message, a
  // fresh touch); callers with no label keep the terse `Ns` for back-compat.
  if (seconds < 60) return nowLabel ?? `${Math.max(1, seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `${weeks}w`;
  return `${Math.floor(days / 365)}y`;
}

/**
 * The Inbox card's timestamp — the terse short unit worn with a localized "ago" ("2d ago"), or the
 * localized "Now" under a minute (Figma `17937:265653`). Built on {@link shortRelativeTime} so the
 * unit ladder stays single-sourced; the caller supplies the localized `ago` template because this
 * module is i18n-free. Mirrors iOS/Android `agoRelativeTime`. Blank when there is no timestamp.
 */
export function agoRelativeTime(
  at: string | Date | undefined,
  now: Date = new Date(),
  nowLabel: string,
  ago: (short: string) => string,
): string {
  const short = shortRelativeTime(at, now, nowLabel);
  if (!short || short === nowLabel) return short;
  return ago(short);
}

/**
 * The long form the detail screen shows against "Last contacted" — "2 days ago".
 *
 * Separate from the short form rather than derived from it: the row needs a column that does not
 * move, and the detail row is prose. Sharing one function would force one of them to read wrong.
 */
export function longRelativeTime(at: string | Date | undefined, now: Date = new Date()): string {
  const then = toDate(at);
  if (!then) return '';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const units: [number, string][] = [
    [60, 'minute'],
    [60, 'hour'],
    [24, 'day'],
    [7, 'week'],
  ];
  let value = seconds;
  let label = 'second';
  for (const [size, name] of units) {
    if (value < size) break;
    value = Math.floor(value / size);
    label = name;
  }
  return `${value} ${label}${value === 1 ? '' : 's'} ago`;
}

/**
 * Whether an entry belongs under the given chip.
 *
 * The attention chip is `contactStage === needsYou` — the top section's own rule — so the chip and the
 * section it shares a name with can never disagree about who belongs in it.
 */
export function matchesChip(entry: ContactRowEntry, chip: ContactChip, isNew?: (entry: ContactRowEntry) => boolean): boolean {
  // The attention chip is the same rule as the top section it shares a name with (see `sectionsOf`),
  // so the chip and the section can never disagree about who belongs in it.
  if (chip === 'needs-attention' || chip === 'action-required') return contactStage(entry) === 'needsYou';
  if (chip === 'new') return isNew ? isNew(entry) : false;
  return true;
}

/**
 * The two sections the design splits the list into.
 *
 * **The top section is the people who need the owner — `contactStage === 'needsYou'`**, labelled
 * `contacts.detail.needsYou` (Q14). The stage is the server's (an open Decision on one of their
 * threads), so a contact whose only live thread is one WE are waiting on is never filed under it: a
 * row whose dot contradicts the section it sits in is worse than either being wrong alone.
 *
 * Everyone else falls to "All Contacts", and nobody appears twice.
 */
export function sectionsOf<T extends ContactRowEntry>(entries: T[]): { needsAttention: T[]; all: T[] } {
  // Generic so a caller holding a RICHER row keeps its own type through the split — the docs above
  // already promised that of `needsAttention`, and narrowing to `ContactRowEntry` here forced every
  // caller to cast the result back to the thing it passed in.
  const attention: T[] = [];
  const all: T[] = [];
  for (const entry of entries) {
    if (contactStage(entry) === 'needsYou') attention.push(entry);
    else all.push(entry);
  }
  return { needsAttention: attention, all };
}

/**
 * Whether a contact matches a local search — a case-insensitive substring of the display name (or a
 * label). The map-home embed still filters its loaded list with this. The Outreach CONTACTS tab does
 * not: its search goes to the server (`useContactBook(search)`), which resolves against the whole people
 * book and DOES match people who already have outreach — verified against production, which is what
 * retired the client-side filter that used to stand in for it there. Shared 1:1 with iOS
 * `ContactRows.matchesSearch` and Android `ContactRows.matchesSearch`.
 */
export function matchesSearch(entry: ContactRowEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = entry.contact?.displayName?.toLowerCase() ?? '';
  if (name.includes(q)) return true;
  return (entry.contact?.labels ?? []).some((label) => label.toLowerCase().includes(q));
}

/**
 * Whether an INBOX row matches the search — the same contact test as `matchesSearch` (display name or a
 * label) OR the shown last message. The inbox row is a channel's last thing said, so typing part of a
 * reply finds the row even when the contact's name doesn't match. Filtering here (per row) rather than
 * per contact is what lets a search hit one channel of a person without pulling in their other channels.
 * Shared 1:1 with iOS/Android `ContactRows.inboxRowMatchesSearch`.
 */
export function inboxRowMatchesSearch(
  row: { contact?: { displayName?: string; labels?: string[] }; lastMessage?: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if ((row.contact?.displayName ?? '').toLowerCase().includes(q)) return true;
  if ((row.contact?.labels ?? []).some((label) => label.toLowerCase().includes(q))) return true;
  return (row.lastMessage ?? '').toLowerCase().includes(q);
}

/**
 * The empty BOOK — no contacts at all, as opposed to a no-results search or a failed/still-loading
 * read. It drives the illustrated empty state and the "Add a new contact" CTA on the Outreach screen:
 * a failed load or an empty search must NOT show it, or a user who actually has contacts (or whose
 * fetch just broke) would be told they have none and nudged to add one.
 *
 * Shared 1:1 with iOS `ContactRows.isEmptyBook` and Android `ContactRows.isEmptyBook`.
 */
export function isEmptyBook(ready: boolean, count: number, searching: boolean): boolean {
  return ready && count === 0 && !searching;
}

// One collator per locale, not one per comparison: `bookTail` sorts the one list on the screen that
// grows without bound, a page at a time.
const collators = new Map<string, Intl.Collator>();
function collatorFor(locale: string | undefined): Intl.Collator {
  const key = locale ?? '';
  let collator = collators.get(key);
  if (!collator) {
    // `base`: case- AND accent-insensitive — "álvaro" sorts with "alvaro", "bea" with "Bea".
    collator = new Intl.Collator(locale, { sensitivity: 'base' });
    collators.set(key, collator);
  }
  return collator;
}

/**
 * The rows the Contacts tab shows AFTER the outreach-derived head: the people book's own pages
 * (`/api/contact-book` with `includeBook` / `bookCursor`), minus everyone the head already shows.
 *
 * The head alone is only the people the user has exchanged outreach with, so a user whose book held
 * phonebook imports or agent-added people but no outreach at all saw an EMPTY Contacts tab while the
 * agent told them the person was "already in your contacts". The book closes that, and the head still
 * leads, because those are the people with something going on.
 *
 * - A book row whose `personId` matches ANY head entry is dropped: the book is the whole book, so it
 *   includes people the head already shows, and the head's copy is the richer one (threads, badge, time).
 * - The book is deduped by `personId`, first copy kept — pages are cursor-joined here, and a person on
 *   two pages must not render twice (or collide as a React key). A row with no `personId` is kept as is.
 * - Sorted by display name, case- AND accent-insensitive, locale-aware — the server's slug order is only
 *   roughly alphabetical, and does not follow a rename. A row with no name sorts after every named one,
 *   ordered among themselves by their first channel value (what the row shows in place of a name, see
 *   `contactDisplayName`); `personId` is the final tiebreak so equal names never swap between renders.
 *
 * Shared 1:1 with iOS `ContactRows.bookTail` and Android `ContactRows.bookTail`.
 */
export function bookTail<T extends ContactRowEntry & { channels?: { value?: string }[] }>(
  head: ContactRowEntry[],
  book: T[],
  locale?: string,
): T[] {
  const seen = new Set<string>();
  for (const entry of head) {
    const id = entry.contact?.personId;
    if (id) seen.add(id);
  }
  // Each row's sort key is derived ONCE, not inside the comparator.
  const keyed: { entry: T; named: boolean; key: string; personId: string }[] = [];
  for (const entry of book) {
    const personId = entry.contact?.personId ?? '';
    if (personId) {
      if (seen.has(personId)) continue;
      seen.add(personId);
    }
    const name = entry.contact?.displayName?.trim() ?? '';
    const key = name || entry.channels?.find((channel) => channel.value)?.value || '';
    keyed.push({ entry, named: !!name, key, personId });
  }
  const collator = collatorFor(locale);
  keyed.sort((a, b) => {
    if (a.named !== b.named) return a.named ? -1 : 1;
    const order = collator.compare(a.key, b.key);
    if (order !== 0) return order;
    return a.personId < b.personId ? -1 : a.personId > b.personId ? 1 : 0;
  });
  return keyed.map((row) => row.entry);
}

/** The Outreach screen's tabs. `calls` is drawn only behind `symphonyOutreachCallTab`, and is never the
 *  default. Shared 1:1 with iOS/Android `ContactRows.OutreachTab`. */
export type OutreachTab = 'contacts' | 'inbox' | 'calls';

/**
 * Which tab the Outreach screen opens on: the INBOX when anybody in the head has a conversation at all
 * — outbound or inbound, open OR closed, because a finished exchange is still correspondence the Inbox
 * lists — else CONTACTS. A user with no outreach yet has an Inbox with nothing in it, and opening them
 * onto an empty tab while their people sit one tap away is the screen hiding the only thing it has.
 *
 * The caller decides this ONCE, when real data first arrives, and never over a tab the reader has
 * already picked. Shared 1:1 with iOS/Android `ContactRows.defaultTab`.
 */
export function defaultTab(entries: ContactRowEntry[]): OutreachTab {
  return entries.some((entry) => (entry.outbound?.length ?? 0) > 0 || (entry.inbound?.length ?? 0) > 0) ? 'inbox' : 'contacts';
}

/**
 * The subtitle under a name on a list row.
 *
 * Prefers what the person is waiting on the viewer for, because that is the actionable half; falls
 * back to the open loop the server picked. Empty when neither applies, and the row then shows the
 * name alone rather than a blank line.
 */
export function rowSubtitle(entry: ContactRowEntry): string {
  return entry.contact?.attentionSummary ?? '';
}

/**
 * The i18n key naming where a contact came from, or null when there is nothing to translate.
 *
 * `Person.source` is free text (`StringValue`, maxLength 40), NOT an enum — the four values below
 * are just the ones anything writes today (`phone` from the contacts import, `outreach` and
 * `calendar` from their consumers, `agent`). So a caller must render the RAW value when this
 * returns null: a closed map would blank out the row the day a fifth source appears, and this field
 * is one string literal in one service away from having one.
 */
export function sourceLabelKey(source: string | undefined): string | null {
  const known = ['phone', 'outreach', 'calendar', 'agent'];
  const raw = (source ?? '').trim().toLowerCase();
  // `phone-removed`: the user deleted the phonebook entry, and People kept the person because it carries
  // what the user or the agent learned about them. It still came from the phone, so it reads as the phone.
  const value = raw === 'phone-removed' ? 'phone' : raw;
  return known.includes(value) ? `contacts.detail.source.${value}` : null;
}

/**
 * A message with the outreach envelope's `Message from <name>: ` prefix removed.
 *
 * Every outreach body in production opens with it — it is addressed to the RECIPIENT, whose client
 * needs telling who is writing. On this screen the reader already knows: they are looking at that
 * person's own page, and each bubble is already on its speaker's side. So the prefix is pure noise
 * that costs a preview row most of its width.
 *
 * Deliberately narrow: it strips only that exact envelope, at the very start, with a short name.
 * Anything else is returned untouched — a body that merely contains a colon must not be truncated.
 */
export function strippedMessageText(text: string | undefined): string {
  const value = (text ?? '').trim();
  // BOTH forms occur, and roughly half the corpus is `Reply from`. Matching only `Message from`
  // stripped outbound turns and left every reply wearing its envelope.
  const match = /^(?:Message|Reply) from [^:\n]{0,100}:\s*/.exec(value);
  return match ? value.slice(match[0].length).trim() : value;
}

/**
 * The line under a thread row's date — the latest thing said on it.
 *
 * Secondary, because it cannot identify the thread: an outreach has no subject and one cannot be
 * derived (in production every body opens `Hi,` followed by the same "I'm <agent>, <owner>'s AI
 * assistant" boilerplate, so any first-line heuristic yields a list of identical rows). The row is
 * titled by its creation date instead — see `threadDateParts`.
 */
export function threadPreview(thread: { lastMessage?: string }): string {
  return strippedMessageText(thread.lastMessage);
}

/**
 * How a thread row titles itself: its real subject when it has one, else the date it was opened.
 *
 * The subject is what the assistant wrote into the message metadata when it composed the outreach
 * (`smsSubject`/`emailSubject`), served by the BFF. It is often ABSENT — the in-app channel writes
 * none — so the date fallback is the normal case, not an error path, and both have to read well.
 *
 * Empty only when there is neither, which is why the caller can render this directly.
 */
/*
 * ⚠️ WRITTEN-DOWN EXCEPTION — a FILE SHARE is not distinguishable on this screen, on ANY of the three
 * surfaces, and it is a DATA-CONTRACT gap rather than a decision.
 *
 * `Outreach.shared_file` is what makes a received outreach a share, and it is why the task card, the
 * island quick-view row and the outreach thread all title such a row by the FILE and offer to open it
 * (`outreachRowTitle` / `presentSharedFile` in `lib/outreach-rows.ts`, mirrored on both phones). The
 * contacts screens cannot do the same, because they are fed by a DIFFERENT read: symphony-bff's
 * `ListContacts` → `OutreachThreadRow`, whose fields are `outreachId · channel · lastMessage ·
 * updatedAt · waitingOnMe · status · createdAt · messages · subject` plus the stage facts. There is **no shared-file field
 * on it at all**, and none on `OutreachThreadMessage` either — so from here a share is
 * indistinguishable from an ordinary thread, and the row titles itself by its subject-or-date like any
 * other. Verified on the generated client (common-lib `SymphonyBffClient/types.ts`) and by the absence
 * of any `sharedFile` reference in web's `contacts/`, iOS's `Features/Contacts/` or Android's
 * `features/Contact*.kt`.
 *
 * This is NOT "web has no screen for it" and it is not per-surface: all three read the same RPC and all
 * three are equally blind. Closing it is a VMR change — a `shared_file` field on `OutreachThreadRow`,
 * populated where the thread is assembled — after which this rule grows a share branch and all three
 * contacts screens get it at once. Do not fix it on one surface by cross-referencing `/api/outreaches`
 * (which DOES carry the notice): that would give web a name the phones cannot have, from a second read
 * the phones do not make, which is the drift the three-surface rule exists to stop.
 */
export function threadTitle(thread: { subject?: string; createdAt?: string | Date }, locale?: string): string {
  const subject = (thread.subject ?? '').trim();
  return subject || threadDateLabel(thread.createdAt, locale);
}

/**
 * The date a thread was opened, as the row's fallback title.
 *
 * Absolute, and carrying the TIME as well as the day, because it stands in for a subject and so
 * has to tell two rows apart. Several of the same person's outreaches routinely share a date — three
 * on one day in the data this was built against — and a relative label ("5w") collapses them
 * completely, which is the same failure as the boilerplate first line.
 *
 * Empty when there is no date, so a caller shows nothing rather than an epoch. iOS and Android
 * format the same choice with their own locale APIs (`DateFormatter` / `DateFormat`).
 */
export function threadDateLabel(at: string | Date | undefined, locale?: string): string {
  const date = toDate(at);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Whether a message opens a new run by its speaker — i.e. whether to draw a sender header on it.
 *
 * The chat idiom: consecutive messages from one side show the name once, on the first of the run.
 * Repeating it on every bubble is what makes a two-party thread read as a form rather than a
 * conversation. `index === 0` always opens a run.
 *
 * Who the speaker IS is not carried per message and does not need to be: an outreach has exactly two
 * ends, so `fromMe` plus the contact's own name names both. The server resolves `fromMe` against the
 * caller's side (the same message is FROM_SOURCE on both ends and means opposite things), so the
 * clients never re-derive it.
 */
export function startsSenderRun(messages: ThreadSpeaker[], index: number): boolean {
  if (index <= 0) return true;
  return speakerOf(messages[index]) !== speakerOf(messages[index - 1]);
}

/**
 * The channels an outreach can go out on, in the order the palette assigns them a colour.
 *
 * `channel` is a lowercased free string on the wire, not an enum — the four below are what anything
 * writes today. An unknown one is NOT dropped: it takes the neutral slot and still renders, the same
 * rule `sourceLabelKey` follows, because a fifth channel is one string literal away.
 */
export const CHANNEL_SLOTS = ['whatsapp', 'email', 'app', 'sms', 'gmail_connector'] as const;

export type ChannelSlot = (typeof CHANNEL_SLOTS)[number] | 'other';

/**
 * Which colour a channel wears. One slot per channel so the same channel is the same colour on every
 * surface and on every row — the point is that a thread's medium is recognisable at a glance, which
 * a single grey label could never do.
 *
 * Returns a SLOT, never a colour: the token layer resolves it, so dark mode and any future re-export
 * move all three surfaces at once. Shared 1:1 with the native `channelSlot`.
 */
export function channelSlot(channel: string | undefined): ChannelSlot {
  const value = (channel ?? '').trim().toLowerCase();
  return (CHANNEL_SLOTS as readonly string[]).includes(value) ? (value as ChannelSlot) : 'other';
}

/**
 * The label a channel shows.
 *
 * A known channel is translated; an unknown one renders its RAW value rather than being blanked,
 * for the same reason `sourceLabelKey` returns null instead of a default.
 */
export function channelLabelKey(channel: string | undefined): string | null {
  const slot = channelSlot(channel);
  return slot === 'other' ? null : `contacts.channel.${slot}`;
}

/** The subset of a message the speaker rules read. */
export type ThreadSpeaker = { fromMe?: boolean; senderName?: string };

/**
 * A stable identity for whoever wrote a message, used only to compare two of them.
 *
 * Prefers the envelope name over `fromMe`, because `fromMe` is WRONG on older messages: they carry
 * no direction, so the server attributes them to the other party and a `fromMe`-only comparison
 * folds a whole legacy thread into one run under the wrong speaker. The envelope survives there.
 */
function speakerOf(message: ThreadSpeaker | undefined): string {
  const name = (message?.senderName ?? '').trim();
  if (name) return `name:${name.toLowerCase()}`;
  return message?.fromMe ? 'side:me' : 'side:them';
}

/**
 * The name to show above a run, or empty to show none.
 *
 * Prefers the envelope's own name; falls back to the two names a thread has — the viewer and the
 * contact — since an outreach has exactly two ends. Empty when neither is known, and the bubble's
 * side then carries the attribution on its own.
 */
export function senderLabel(message: ThreadSpeaker | undefined, names: { me: string; them: string; myAgent?: string; theirAgent?: string }): string {
  // An outreach is two ASSISTANTS corresponding, not two people typing: this side's Maestro wrote
  // the outbound messages and the other side's agent wrote the replies. So the agent's name is the
  // honest attribution, and the person's name is the fallback for the very common case where the
  // counterparty runs no assistant (they are not a Symphony user) — there the message really is
  // from them, relayed.
  //
  // The envelope is deliberately NOT preferred here any more. It names the person the outreach is
  // FROM ("Message from Amir Harel"), which is who it is on behalf of, not who composed it.
  const mine = messageIsMine(message, names.me);
  const agent = (mine ? names.myAgent : names.theirAgent)?.trim();
  if (agent) return agent;

  const envelope = (message?.senderName ?? '').trim();
  if (envelope) return envelope;
  return (mine ? names.me : names.them).trim();
}

/**
 * Which side of the transcript a message sits on.
 *
 * The envelope wins over `fromMe` for the same reason `speakerOf` prefers it — on a legacy message
 * `fromMe` is simply wrong, and a chat view puts every one of those on the wrong side. When the
 * envelope names the viewer, the message is theirs whatever the missing direction implies.
 */
export function messageIsMine(message: ThreadSpeaker | undefined, myName: string): boolean {
  // `fromMe` is the server-resolved direction — trust it when it says the message is ours. The sender
  // name is only a FALLBACK for older messages that carry no direction (fromMe absent/false), which the
  // server attributes to the other party; a matching name pulls those back to our side. Checking the
  // name FIRST (as this did) mis-files our own messages as "theirs" whenever the sender name is not our
  // name — and it rarely is exactly our name: it is our AGENT's name, which need not equal the reader's
  // recipient name and changes the moment the agent is renamed (a message sent as "Señor Banana" then
  // read after a rename to "Señor Bananaush" flipped to the received side).
  if (message?.fromMe) return true;
  const envelope = (message?.senderName ?? '').trim().toLowerCase();
  const mine = myName.trim().toLowerCase();
  return !!envelope && !!mine && envelope === mine;
}

/**
 * The label for a file attached to a message.
 *
 * The size is deliberately optional and usually absent — nothing in production sets it — so this
 * returns the name alone rather than "name · 0 B". Bytes are formatted only when a real size arrived.
 */
export function attachmentLabel(attachment: { fileName?: string; size?: number | string }): string {
  const name = (attachment.fileName ?? '').trim();
  const bytes = typeof attachment.size === 'string' ? Number(attachment.size) : attachment.size;
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return name;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${name} · ${rounded} ${units[unit]}`;
}

// ── Threads ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The subset of an outreach thread these rules read: the stage contract's facts (`StageThread`) plus the
 * row's own dates. A thread's stage, group and line are `lib/outreach-stage.ts`'s, never re-derived here.
 */
export type ContactThread = StageThread & {
  outreachId?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  waitingOnMe?: boolean;
};

// ── The flat outreach list ─────────────────────────────────────────────────────────────────────

/**
 * The channel a conversation is DELIVERED on, for the chip — trimmed, and EMPTY when the wire has none.
 *
 * An empty channel is NOT the in-app channel. The bff writes `preferred_communication_channel` lowercased
 * and `""` only for `UNKNOWN_COMMUNICATION_CHANNEL`; an in-app outreach is stored as `APP` (the outreach
 * service resolves a Symphony-user target to APP before it persists, `defaultToAppForFirstPartyTarget`)
 * and arrives as `app`. UNKNOWN is what is left when nothing resolved a channel — a recipient with no
 * stated preference that is not a Symphony user, or a self-outreach — and the outreach service delivers
 * it as an event only, on no medium. Badging that "App" claimed a delivery that never happened, on a
 * thread whose recipient may not even have the app (a live report). So an empty channel draws NO chip:
 * `ChannelChip` renders nothing for `''`, on all three surfaces.
 *
 * It stays a shared RULE rather than an expression inlined at each call site because the list rows, the
 * viewer header, the contact detail and the island all show a thread's channel, and a fourth surface
 * inventing its own default is how "App" appeared in the first place. Shared 1:1 with iOS/Android
 * `ContactRows.threadChannel`. The stage LINE's `{channel}` param is a separate rule
 * (`channelParam` in `outreach-stage.ts`, pinned by `native-apps/shared/outreach-stage/contract.json`)
 * and is deliberately left as it is here.
 *
 * ⚠️ The parameter is NOT optional, deliberately: a caller holding a maybe-thread has to say what absence
 * means, and for every caller so far it means "draw no chip".
 */
export function threadChannel(thread: { channel?: string }): string {
  return (thread.channel ?? '').trim();
}

/**
 * ONE of a contact's outreaches by id, whichever side it arrived on — what the viewer opens.
 *
 * Searches `outbound` first so a self-outreach (its own source AND target) resolves to its outbound copy,
 * the same precedence `uniqueThreads` uses.
 */
export function findOutreach<T extends { outreachId?: string }>(
  entry: { outbound?: T[]; inbound?: T[] },
  outreachId: string,
): T | undefined {
  if (!outreachId) return undefined;
  return [...(entry.outbound ?? []), ...(entry.inbound ?? [])].find((t) => t.outreachId === outreachId);
}

/**
 * The one conversation to open straight from a contact ROW, or null when there is not exactly one.
 *
 * The outreach counterpart of the old `singleActiveChannel`: a contact with a single conversation has
 * one obvious thing to open, so the row opens it in the viewer instead of making the reader take a
 * second step through a screen that lists exactly one row. A contact with several has no single
 * answer, so the row opens the contact screen to choose from.
 *
 * Counts every conversation, closed ones included — because the list the reader would otherwise land
 * on shows those too. "Exactly one" has to mean the same thing here as the number of rows they would
 * see, or the shortcut fires on a screen that turns out to have four entries.
 */
export function singleOutreachId(entry: { outbound?: ChannelThread[]; inbound?: ChannelThread[] }): string | null {
  // Through `uniqueThreads` (Q8): a self-outreach arrives in BOTH lists, and counting both copies made a
  // contact with one conversation look like two.
  const all = uniqueThreads(entry).filter(({ thread }) => !!thread.outreachId);
  return all.length === 1 ? (all[0].thread.outreachId as string) : null;
}

/** A thread plus the channel it was delivered on. */
export type ChannelThread = ContactThread & { channel?: string };


/** A thread as the INBOX list reads it: the stage facts plus what a row shows — the last thing said. */
export type InboxThread = ChannelThread & { lastMessage?: string };

/** One inbox entry (a contact and its threads) as the list carries it. Generic over the card so a
 *  caller holding a richer card (web's `WebContactCard`, with the avatar URL) keeps it on the row. */
export type InboxEntry<C extends { personId?: string } = ContactRowCard> = {
  contact?: C;
  /** The contact's addresses. Read for ONE thing: the display fallback for a contact with no name.
   *  Without it two address-only people both read "Unnamed contact" — on the row and, worse, in the
   *  contact FILTER, where two options with the same label cannot be told apart at all. */
  channels?: { value?: string }[];
  /** Present for a contact the people book knows only by an address (no `Person`); the row opens by it
   *  when there is no person id, mirroring #10660's recipient identity on the Contacts list. */
  recipientId?: string;
  outbound?: InboxThread[];
  inbound?: InboxThread[];
};

/** One inbox ROW — the last message of one contact on ONE channel. Tapping it opens the viewer on that
 *  contact's identity ({@link personId} or, address-only, {@link recipientId}) + channel — the same
 *  conversation the contact detail's "Needs you" rows open. */
export type InboxRow<C extends { personId?: string } = ContactRowCard> = {
  personId: string;
  /** The address-only identity — set (with an empty `personId`) for a contact the book knows only by an
   *  address, so the row opens the same recipient-keyed conversation the Contacts list does. */
  recipientId?: string;
  contact?: C;
  channel: string;
  lastMessage: string;
  lastActivityAt?: string | Date;
  /** The thread's stage — what the dot, the line and the group read (`lib/outreach-stage.ts`). */
  stage: OutreachStage;
  /** The thread itself, for `stageLine` / `stageCard` / `needsYouPrompt`. */
  thread: InboxThread;
  /** Which list it arrived in — the thread header's "started by you / them". */
  direction: 'outbound' | 'inbound';
  /** The conversation this row IS. The row opens it directly — there is no channel roll-up to resolve. */
  outreachId: string;
  /** The contact's first address, carried so an unnamed contact can be named by it. See
   *  {@link inboxRowName}. */
  address?: string;
};

/**
 * What to call the contact on an inbox row: their name, else their address, else "unnamed".
 *
 * The address arm is the point. The row used to pass no channels to {@link contactDisplayName}, so the
 * fallback could not apply and every address-only contact read "Unnamed contact". On a ROW that is
 * merely unhelpful — the message text below it still distinguishes them. In the contact FILTER it is a
 * defect: two options rendering the same label cannot be told apart, so the reader cannot know which
 * one they are selecting. One helper, so the row and the filter can never disagree about a name.
 */
export function inboxRowName<C extends { personId?: string; displayName?: string }>(
  row: { contact?: C; address?: string },
  unnamed: string,
): string {
  return contactDisplayName(row.contact, row.address ? [{ value: row.address }] : undefined, unnamed);
}

/**
 * The Inbox list: ONE ROW PER OUTREACH across every contact, newest-first (Done by `closedAt`), each
 * carrying its thread's stage. The caller groups them with {@link groupInboxRowsByStage}.
 *
 * NOT limited to active outreaches — the inbox is all correspondence — so a finished conversation still
 * shows. Threads go through `uniqueThreads` (a self-outreach arrives in both `outbound` and `inbound`;
 * its outbound copy wins), an empty channel stays empty (no chip — see `threadChannel`), and a thread with no id is
 * dropped (it cannot be opened).
 *
 * Pure; shared 1:1 with iOS/Android `ContactRows.inboxRows`.
 */
export function inboxRows<C extends { personId?: string }>(entries: InboxEntry<C>[]): InboxRow<C>[] {
  const rows: InboxRow<C>[] = [];
  for (const entry of entries) {
    const personId = entry.contact?.personId ?? '';
    const recipientId = entry.recipientId ?? '';
    // Keep a contact the book knows only by an ADDRESS — it has no person id but a real conversation.
    // Only a row with NEITHER identity is unopenable, and dropped.
    if (!personId && !recipientId) continue;
    // ONE ROW PER OUTREACH. This used to roll the outreaches up per CHANNEL and show the newest one's
    // text, which meant a person with three live WhatsApp conversations appeared once, under whichever
    // of them had moved last — the other two were unreachable from here and their state was invisible.
    // A channel is not a conversation; it is how a conversation was delivered.
    for (const { thread, direction } of uniqueThreads(entry)) {
      const outreachId = thread.outreachId ?? '';
      if (!outreachId) continue;
      rows.push({
        personId,
        recipientId: personId ? undefined : recipientId,
        contact: entry.contact,
        outreachId,
        channel: threadChannel(thread),
        lastMessage: threadPreview(thread),
        lastActivityAt: threadTime(thread),
        stage: threadStage(thread),
        thread,
        direction,
        address: entry.channels?.find((channel) => channel.value)?.value,
      });
    }
  }
  // FLAT, newest-first by the same sort key the groups use, so grouping keeps this order.
  return rows.sort((a, b) => stageSortTime(b.thread) - stageSortTime(a.thread));
}

/**
 * The Inbox's CONTACT filter options — one per contact present in the rows, in first-seen order.
 *
 * The labels have to be distinguishable, which is the whole reason this is a rule rather than a `map`.
 * Naming each option by `inboxRowName` alone is not enough: that answers with the display name when
 * there is one, so two different people who are both called "Dana Levi" produce two identical options
 * and the reader cannot tell which one they are about to select. An address-only pair is already
 * separated by the address fallback; a same-NAME pair is not.
 *
 * So a colliding label — and ONLY a colliding label — gets its address appended. Appending it to every
 * option would make the ordinary case noisy for a problem it does not have, and the ordinary case is
 * nearly all of them. Two contacts who share a name AND have no address between them are genuinely
 * indistinguishable from the data in hand; nothing here invents a difference.
 *
 * Shared 1:1 with iOS/Android `ContactRows.inboxContactOptions`.
 */
export function inboxContactOptions<C extends { personId?: string; displayName?: string }>(
  rows: { personId: string; recipientId?: string; contact?: C; address?: string }[],
  unnamed: string,
): { key: string; label: string }[] {
  const byKey = new Map<string, { label: string; address?: string }>();
  for (const row of rows) {
    const key = row.personId || row.recipientId || '';
    if (!key || byKey.has(key)) continue;
    byKey.set(key, { label: inboxRowName(row, unnamed), address: row.address });
  }
  const times = new Map<string, number>();
  for (const { label } of byKey.values()) times.set(label, (times.get(label) ?? 0) + 1);
  return [...byKey].map(([key, { label, address }]) => ({
    key,
    label: (times.get(label) ?? 0) > 1 && address && address !== label ? `${label} · ${address}` : label,
  }));
}

/** The flat inbox rows grouped by stage — `STAGE_GROUPS` order (Needs you · In progress · Done), each
 *  by time desc and Done by `closedAt ?? time`. Every group is returned; the renderer hides an empty one. */
export function groupInboxRowsByStage<C extends { personId?: string }>(rows: InboxRow<C>[]) {
  return groupByStage(rows, (row) => row.thread);
}

// ── Inbox row: time, author, and the "what is narrowing this list" summary ──────────────────────

/**
 * Which form an Inbox row's last-activity time takes, relative to `now` in the device's calendar:
 *  - the SAME calendar day → `time` ("08:00");
 *  - 1–6 calendar days ago → `weekday` ("Mon"). Seven days ago would be today's own weekday again, so it
 *    is a date, never a second "Mon";
 *  - anything else (older, or a future instant on another day — clock skew) → `monthDay` ("Oct 3") in
 *    the current year, `yearMonthDay` ("Oct 3, 2025") in any other.
 *
 * The FORM is the shared rule (pinned 1:1 with iOS/Android `ContactRows.inboxTimeForm`); the formatting
 * is each platform's own locale API, so field order and the 12/24h clock follow the locale.
 */
export type InboxTimeForm = 'time' | 'weekday' | 'monthDay' | 'yearMonthDay';

export function inboxTimeForm(at: Date, now: Date): InboxTimeForm {
  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000);
  if (days === 0) return 'time';
  if (days >= 1 && days <= 6) return 'weekday';
  return at.getFullYear() === now.getFullYear() ? 'monthDay' : 'yearMonthDay';
}

/**
 * The compact, right-aligned time on an Inbox row — when its conversation last moved
 * (`row.lastActivityAt`). See {@link inboxTimeForm} for the ladder. Empty without a timestamp (render
 * nothing, never the epoch). Mirrors iOS/Android `inboxRowTime`.
 */
export function inboxRowTime(at: string | Date | undefined, now: Date = new Date(), locale?: string): string {
  const date = toDate(at);
  if (!date) return '';
  const loc = locale || undefined;
  switch (inboxTimeForm(date, now)) {
    case 'time':
      // The locale's SHORT time — "08:00" in a 24h locale, "8:00 AM" in en-US — the same style the phones'
      // `.short` / `jmm` produce.
      return date.toLocaleTimeString(loc, { timeStyle: 'short' });
    case 'weekday':
      return date.toLocaleDateString(loc, { weekday: 'short' });
    case 'monthDay':
      return date.toLocaleDateString(loc, { month: 'short', day: 'numeric' });
    case 'yearMonthDay':
      return date.toLocaleDateString(loc, { year: 'numeric', month: 'short', day: 'numeric' });
  }
}

/**
 * The name the preview's author prefix uses for the CONTACT: their first name when they have a display
 * name (its first word), else the row's whole fallback (address, or "Unnamed contact") — an address has
 * no "first word" worth showing. Mirrors iOS/Android `InboxRow.firstName(unnamed:)`.
 */
export function inboxRowFirstName<C extends { personId?: string; displayName?: string }>(
  row: { contact?: C; address?: string },
  unnamed: string,
): string {
  const named = (row.contact?.displayName ?? '').trim();
  if (named) return named.split(/\s+/)[0];
  return inboxRowName(row, unnamed);
}

/**
 * Who wrote an Inbox row's preview, as the prefix it wears ("The Maverick", "Dana", "Dana's agent"), or
 * '' for none. Off the wire's `lastAuthor` (symphony-bff `OutreachMessageAuthor` — "who wrote the tail
 * message, from the caller's point of view"):
 *  - AGENT → the owner's agent; OWNER_NOTE reads as the agent too, the same fold `resolvedSpeaker` makes;
 *  - CONTACT → the contact's first name; CONTACT_AGENT → "{name}'s agent";
 *  - UNKNOWN / absent / unrecognised → '' — no prefix. Deliberately NOT the `waitingOnMe` stand-in the
 *    stage line uses: a guessed author on the message itself would be a wrong attribution, not a hint.
 * The caller resolves the three labels (the agent's live name, the i18n template); this only picks.
 * Shared 1:1 with iOS/Android `ContactRows.inboxPreviewAuthor`.
 */
export function inboxPreviewAuthor(
  lastAuthor: string | null | undefined,
  labels: { agent: string; contact: string; contactAgent: string },
): string {
  switch (parseAuthor(lastAuthor)) {
    case 'agent':
    case 'ownerNote':
      return labels.agent.trim();
    case 'contact':
      return labels.contact.trim();
    case 'contactAgent':
      return labels.contactAgent.trim();
    default:
      return '';
  }
}

/**
 * The one-line "what is narrowing this list" summary above the Inbox results, or null when nothing is.
 *
 * Exists because the search is RESTORED (web from the URL's `iq`, Android across navigation): an owner
 * opened the Inbox to "Done · 1" and one row, and read it as 126 of 127 conversations missing — the field
 * still held an old query. So whenever a query OR any filter narrows the list, the list says so, with a
 * Clear beside it. `count` is the number of rows actually shown. A query names itself
 * (`outreach.inbox.results.query.*`, "1 match for “…”"); filters alone read just the count. Plural by the
 * catalog's `.one` / `.other` convention. `query` comes back trimmed AND bidi-isolated ({@link bidiIsolate}),
 * ready to interpolate. Shared 1:1 with iOS/Android `ContactRows.inboxResultSummary`.
 */
export function inboxResultSummary(
  query: string,
  filtersActive: boolean,
  count: number,
): { key: string; count: number; query: string } | null {
  const q = query.trim();
  if (!q && !filtersActive) return null;
  const plural = count === 1 ? 'one' : 'other';
  return { key: q ? `outreach.inbox.results.query.${plural}` : `outreach.inbox.results.${plural}`, count, query: q ? bidiIsolate(q) : '' };
}

/**
 * `text` wrapped in FIRST STRONG ISOLATE (U+2068) … POP DIRECTIONAL ISOLATE (U+2069), so it formats into a
 * sentence as its own directional run. Without it a Hebrew query inside "3 matches for “…”" (or a Latin one
 * inside the Hebrew sentence) reorders the surrounding quotes and count. Shared 1:1 with iOS/Android
 * `ContactRows.bidiIsolate`.
 */
export function bidiIsolate(text: string): string {
  return `\u2068${text}\u2069`;
}

function toDate(at: string | Date | undefined): Date | undefined {
  if (!at) return undefined;
  const date = at instanceof Date ? at : new Date(at);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
